import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import React from 'react';
import { useLocation } from 'react-router-dom';
import * as Utils from '../util/Utils'
import * as ATCAConstants from '../util/ATCAConstants';
import { get, post } from 'aws-amplify/api';

import 'dayjs/locale/en-gb';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

export const ProjectContext = React.createContext({
  project: {},
  correlatorSettings: [] as any[],
  correlatorConfigurations: [] as any[],
  setCorrelatorConfigurations: (_config: any[]) => {},
  scheduleFileList: [] as string[],
  getTargets: () => [] as any[],
  setTargets: (_targets: any[], save: boolean = false) => { },
  targetFileContent: '',
  saveTargetFile: () => { },
  autoSave: true as boolean,
  setAutoSave: (_isAutoSave: boolean) => { },

  integrationTime: '' + ATCAConstants.DEFAULT_INTEGRATION_TIME_S,
  setIntegrationTime: (_integrationTime: string) => { },

  utcDate: dayjs.utc(),
  setUTCDate: (_date: Dayjs) => { },
  saveCorrelatorConfiguration: () => { },
  saveFile: (_filename: string, _content: any) => { },
  hasSubBands: (_corrSetting: any): boolean => { return false },
  getCorrelatorSetting: (_name: string, _receiverName: string): any => { return {} },
  getCorrelatorConfiguration: (_name: string): any => { return {} },
  calculateDataRate: (_name: string, _receiverName: string): number => { return 0 },
});

// dayjs.extend(require('dayjs/plugin/utc'));

export default function ProjectContextProvider(props: any) {
  const location = useLocation();

  const [snackMessage, setSnackMessage] = React.useState<any>(null);
  const [autoSave, setAutoSave] = React.useState(true);
  const [integrationTime, setIntegrationTime] = React.useState('' + ATCAConstants.DEFAULT_INTEGRATION_TIME_S);

  const [project, setProject] = React.useState<any>({});

  const [correlatorConfigurations, setCorrelatorConfigurations] = React.useState<any[]>([]);

  const [scheduleFileList, setScheduleFileList] = React.useState<string[]>([]);
  const [targetFileContent, setTargetFileContent] = React.useState<string>('');
  const [utcDate, setUTCDate] = React.useState(dayjs.utc());
  const [correlatorSettings, setCorrelatorSettings] = React.useState<any[]>([]);

  React.useEffect(() => {
    const getOperation = get(
      {
        apiName: 'cabb',
        path: '/get_correlator_settings',
        options: {
          queryParams: {
            integration_time: integrationTime
          }
        }
      }
    );

    getOperation.response
      .then(response => response.body.json())
      .then((response: any) => {        
        if (response['status'] === 'fail') {
          let message = response['message'] || '';
          setSnackMessage({
            severity: 'error',
            message: 'Could not retrieve setting from server: ' + message
          });
        } else {
          setCorrelatorSettings(response);
        }
      })
      .catch((err: Error) => {
        setSnackMessage({
          severity: 'error',
          message: 'Could not retrieve correlator settings from server!'
        });
      });

  }, [integrationTime]);

  React.useEffect(() => {
    // no need to retrieve project if root
    if (location.pathname === '/')
      return;

    // retrieve project from the backend
    const queryParams = new URLSearchParams(location.search);
    const projectName = queryParams.get('project');
    if (!projectName) {
      setSnackMessage({
        severity: 'error',
        message: 'No project provided!'
      });
      return;
    }

    const getOperation = get(
      {
        apiName: 'cabb',
        path: '/retrieve_project',
        options: {
          queryParams: {
            project: projectName
          }
        }          
      }
    );

    getOperation.response
      .then(response => response.body.json())
      .then((response: any) => {
    
        if (response['status'] === 'success') {
          setProject(response['project']);
          setCorrelatorConfigurations(response['correlator_configuration_file']);
          setScheduleFileList(response['schedule_files']);
          setTargetFileContent(response['target_file']);
        } else {
          let message = response['message'] || '';
          setSnackMessage({
            severity: 'error',
            message: 'Could not retrieve project from server: ' + message
          });
        }
      })
      .catch((err: Error) => {
        setSnackMessage({
          severity: 'error',
          message: 'Could not retrieve project from server!'
        });
      });
  }, [location]);

  const saveCorrelatorConfiguration = () => {
    const queryParams = new URLSearchParams(location.search);
    const projectName = queryParams.get('project');
    let filename = project['correlator_configuration_file'];
    filename = projectName + '/' + (filename ? filename : 'correlator_configuration.json');
    saveFile(filename, correlatorConfigurations);
  }

  const handleSetTargetFile = (targets: any[], save: boolean = false) => {
    let count = targets.length;
    if (targets.length > ATCAConstants.MAX_TARGETS_COUNT) {
      count = ATCAConstants.MAX_TARGETS_COUNT;
      setSnackMessage({
        severity: 'error',
        message: `Too many targets. Only the first ${ATCAConstants.MAX_TARGETS_COUNT} are saved.`
      });
    }

    const content: string[] = [];
    for (let i=0; i<count; i++) {
      const str = Utils.targetToString(targets[i]);
      content.push(str);
    }
    const targetFile = content.join('\n');
    setTargetFileContent(targetFile);

    if (save) {
      handleSaveTargetFile(targetFile);
    }
  }

  const getTargets = (): any[] => {
    return Utils.getTargets(targetFileContent);
  }

  const saveTargetFile = () => {
    handleSaveTargetFile(targetFileContent);
  }

  const handleSaveTargetFile = (content: string) => {
    const queryParams = new URLSearchParams(location.search);
    const projectName = queryParams.get('project');
    const targetfilename = project['target_file'];
    const filename = projectName + '/' + (targetfilename ? targetfilename : 'targets.csv');
    saveFile(filename, content);
  }

  const saveFile = (filename: string, content: any) => {
    const data = {
      filename: filename,
      content: content
    };
    
    const postOperation = post(
      {
        apiName: 'cabb',
        path: '/save_file',
        options: {
          body: data,
          headers: {'content-type': 'application/json'}
        }
      }
    );

    postOperation.response
      .then(response => response.body.json())
      .then((response: any) => {
  
        if (response['status'] === 'success') {
          setSnackMessage({
            severity: 'success',
            message: filename + ' changes saved!'
          });
        } else {
          const message = response['message'];
          if (message)
            setSnackMessage({
              severity: 'error',
              message: message
              });
        }
      })
      .catch((err: Error) => {
        setSnackMessage({
          severity: 'error',
          message: 'Could not save changes!'
        });
      });
  }
  
  const calculateDataRate = (name: string, receiverName: string) => {
    // get data rate of correlator settings
    let dataRate = 0;
    const correlatorConfig = getCorrelatorConfiguration(name) || {};
    const correlatorSetting = getCorrelatorSetting(
      correlatorConfig['correlator_setting'], receiverName
    );

    if (correlatorSetting) {
      dataRate = correlatorSetting['data_rate'] || 0;
    }

    // add all the zoom band data rates
    for (const zoom of (correlatorConfig['sub_band_configuration'] || [])) {
      const name = zoom['zoom'];
      const band = Utils.getZoomBand(
        correlatorSettings,
        correlatorConfig['correlator_setting'],
        name) || {};

      dataRate += band['data_rate'] || 0;
    }

    return dataRate;
  }

  const getCorrelatorConfiguration = (name: string) => {
    const configs = correlatorConfigurations.filter((config: any) => {
      return config['name'] === name;
    });

    return configs.length === 1 ? configs[0] : {};
  }

  const getCorrelatorSetting = (name: string, receiverName: string) => {
    const settings = correlatorSettings.filter((settings: any) => {
      return settings['name'] === name && 
      settings['compatible_receivers'][receiverName];
    });

    return settings.length === 1 ? settings[0] : {};
  }


  const hasSubBands = (corrSetting: any) => {
    // corrmode is valid
    // corrconfig is valid
    // and corrconfig has zoom or spectral windows
    const frequency_configuration = corrSetting['frequency_configuration'];

    if (Object.keys(frequency_configuration).length < 1)
      return false;

    const corrConfigName = corrSetting['correlator_setting'];
    const receiverName = corrSetting['frequency_configuration']['receiver'];
    const corrConfig = getCorrelatorSetting(corrConfigName, receiverName);
    
    if (Object.keys(corrConfig).length < 1)
      return false;

    return corrConfig.number_of_spectral_windows > 0 ||
      corrConfig.number_of_zoom_windows > 0;
  }


  return (<ProjectContext.Provider value={{
    project: project,
    correlatorSettings: correlatorSettings,
    getCorrelatorSetting: getCorrelatorSetting,
    getCorrelatorConfiguration: getCorrelatorConfiguration,
    calculateDataRate: calculateDataRate,
    correlatorConfigurations: correlatorConfigurations,
    setCorrelatorConfigurations: setCorrelatorConfigurations,
    scheduleFileList: scheduleFileList,
    getTargets: getTargets,
    setTargets: handleSetTargetFile,
    saveTargetFile: saveTargetFile,
    targetFileContent: targetFileContent,
    autoSave: autoSave,
    setAutoSave: setAutoSave,

    integrationTime: integrationTime,
    setIntegrationTime: setIntegrationTime,

    utcDate: utcDate,
    setUTCDate: setUTCDate,
    saveCorrelatorConfiguration: saveCorrelatorConfiguration,
    saveFile: saveFile,
    hasSubBands: hasSubBands,

  }}>
    {props.children}

    <Snackbar
      role = "alert"
      aria-label = {"Alert message"}
      open={Boolean(snackMessage)}
      autoHideDuration={6000}
      onClose={e => setSnackMessage(null)}
    >
      <Alert severity={snackMessage ? snackMessage.severity : 'error' }>
        {snackMessage ? snackMessage.message : ''}
      </Alert>
    </Snackbar>

  </ProjectContext.Provider>);
}
